import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const AddCredentialModal = ({ isOpen, onClose, onAdd }) => {
  const [formData, setFormData] = useState({
    title: '',
    type: '',
    issuer: '',
    issueDate: '',
    expirationDate: '',
    credentialFile: null
  });
  const [isLoading, setIsLoading] = useState(false);

  const credentialTypes = [
    { value: 'vaccination', label: 'Vaccination Record' },
    { value: 'insurance', label: 'Insurance Coverage' },
    { value: 'training', label: 'Training Certificate' },
    { value: 'medical', label: 'Medical Qualification' },
    { value: 'license', label: 'Professional License' }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleFileChange = (e) => {
    const file = e?.target?.files?.[0];
    setFormData(prev => ({
      ...prev,
      credentialFile: file
    }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setIsLoading(true);

    try {
      // Mock credential processing
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      const newCredential = {
        id: Date.now()?.toString(),
        title: formData?.title,
        type: formData?.type,
        issuer: formData?.issuer,
        issueDate: formData?.issueDate,
        expirationDate: formData?.expirationDate,
        status: 'pending',
        blockchainVerified: false,
        verificationHash: `0x${Math.random()?.toString(16)?.substr(2, 64)}`,
        issuerLogo: "https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=100&h=100&fit=crop&crop=center",
        issuerType: "Healthcare Organization",
        details: [
          { label: "Credential ID", value: `CRED-${Date.now()}` },
          { label: "Verification Method", value: "Digital Signature" },
          { label: "Blockchain Network", value: "Ethereum Mainnet" }
        ]
      };

      onAdd(newCredential);
      onClose();
      
      // Reset form
      setFormData({
        title: '',
        type: '',
        issuer: '',
        issueDate: '',
        expirationDate: '',
        credentialFile: null
      });
    } catch (error) {
      console.error('Error adding credential:', error);
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-1200 p-4">
      <div className="bg-card border border-border rounded-lg max-w-md w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <h2 className="text-xl font-semibold text-foreground">Add New Credential</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-muted rounded-lg transition-clinical"
          >
            <Icon name="X" size={20} className="text-muted-foreground" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Credential Title */}
          <Input
            label="Credential Title"
            type="text"
            placeholder="e.g., COVID-19 Vaccination Certificate"
            value={formData?.title}
            onChange={(e) => handleInputChange('title', e?.target?.value)}
            required
          />

          {/* Credential Type */}
          <Select
            label="Credential Type"
            placeholder="Select credential type"
            options={credentialTypes}
            value={formData?.type}
            onChange={(value) => handleInputChange('type', value)}
            required
          />

          {/* Issuer */}
          <Input
            label="Issuing Organization"
            type="text"
            placeholder="e.g., City Health Department"
            value={formData?.issuer}
            onChange={(e) => handleInputChange('issuer', e?.target?.value)}
            required
          />

          {/* Issue Date */}
          <Input
            label="Issue Date"
            type="date"
            value={formData?.issueDate}
            onChange={(e) => handleInputChange('issueDate', e?.target?.value)}
            required
          />

          {/* Expiration Date */}
          <Input
            label="Expiration Date"
            type="date"
            description="Leave empty if credential doesn't expire"
            value={formData?.expirationDate}
            onChange={(e) => handleInputChange('expirationDate', e?.target?.value)}
          />

          {/* File Upload */}
          <div>
            <Input
              label="Credential Document"
              type="file"
              description="Upload PDF, image, or digital credential file"
              onChange={handleFileChange}
              accept=".pdf,.jpg,.jpeg,.png,.json"
            />
            {formData?.credentialFile && (
              <div className="mt-2 p-3 bg-muted rounded-lg">
                <div className="flex items-center space-x-2">
                  <Icon name="FileText" size={16} className="text-muted-foreground" />
                  <span className="text-sm text-foreground">{formData?.credentialFile?.name}</span>
                </div>
              </div>
            )}
          </div>

          {/* Processing Info */}
          <div className="bg-accent/10 border border-accent/20 rounded-lg p-4">
            <div className="flex items-start space-x-2">
              <Icon name="Info" size={16} className="text-accent mt-0.5" />
              <div>
                <p className="text-sm font-medium text-foreground">Verification Process</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Your credential will be verified using blockchain technology and zero-knowledge proofs. 
                  This process may take a few minutes to complete.
                </p>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-between pt-4">
            <Button variant="ghost" onClick={onClose} disabled={isLoading}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="default"
              loading={isLoading}
              iconName="Plus"
              iconPosition="left"
            >
              {isLoading ? 'Processing...' : 'Add Credential'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddCredentialModal;